import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { buildPublicQualificationRows } from "@/lib/qualificationPreview";
import { cn } from "@/lib/utils";

const previewCache = new Map();
const produitCache = new Map();

const OPEN_DELAY_MS = 160;
const CLOSE_DELAY_MS = 220;

const FLIP_SHELL = {
  minH: "min-h-[280px]",
  rounded: "rounded-2xl",
  maxRows: 14,
  headerPy: "py-2",
};

function useFinePointerHover() {
  const [ok, setOk] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const apply = () => setOk(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);
  return ok;
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduced(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);
  return reduced;
}

function customFieldsHasValues(d) {
  const cf = d?.custom_fields;
  if (!cf || typeof cf !== "object" || Array.isArray(cf)) return false;
  return Object.entries(cf).some(([, v]) => {
    if (v == null || v === "") return false;
    if (typeof v === "boolean") return true;
    return String(v).trim() !== "";
  });
}

function QualificationPanelBody({ dossier, produit, loading, error, className }) {
  const qualifRows = useMemo(
    () =>
      buildPublicQualificationRows(dossier, produit, {
        hideDeptDuplicate: false,
        maxRows: 14,
        showEmpty: Boolean(produit?.champs?.length),
      }),
    [dossier, produit],
  );

  if (error) {
    return <p className={cn("px-3 py-4 text-center text-sm text-red-600", className)}>{error}</p>;
  }

  if (loading && qualifRows.length === 0) {
    return (
      <div
        className={cn(
          "flex flex-1 items-center justify-center gap-2 px-3 py-6 text-sm text-slate-500",
          className,
        )}
      >
        <Loader2 className="h-4 w-4 animate-spin text-[#2D7FF9]" aria-hidden />
        Chargement des critères…
      </div>
    );
  }

  if (qualifRows.length === 0) {
    return (
      <p className={cn("px-3 py-4 text-sm italic text-slate-400", className)}>
        Aucun critère de qualification renseigné.
      </p>
    );
  }

  return (
    <dl className={cn("space-y-1.5 px-3 py-2", className)}>
      {qualifRows.map((row) => (
        <div key={row.nom} className="rounded-md bg-slate-50/95 px-2.5 py-1.5 ring-1 ring-slate-100">
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{row.nom}</dt>
          <dd className="text-[13px] font-medium leading-snug text-slate-900 break-words">{row.val}</dd>
        </div>
      ))}
    </dl>
  );
}

function QualificationBackFace({
  dossier,
  produit,
  loading,
  error,
  linkHref,
  title,
  onBeforeOpen,
  onPointerEnter,
  onPointerLeave,
  flipFace = true,
  shell = FLIP_SHELL,
}) {
  const { minH, rounded, maxRows, headerPy } = shell;
  return (
    <Link
      to={linkHref}
      onClick={() => onBeforeOpen?.()}
      className={cn(
        "flex h-full flex-col overflow-hidden border border-[#2D7FF9]/35 bg-white text-left shadow-[0_12px_40px_-12px_rgba(45,127,249,0.3)] transition-colors hover:border-[#2D7FF9]/50",
        minH,
        rounded,
        flipFace
          ? "absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)]"
          : "relative h-full w-full",
      )}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      aria-label={title ? `Ouvrir l’annonce — ${title}` : "Ouvrir l’annonce"}
      data-testid="dossier-card-open-back"
    >
      <div
        className={cn(
          "flex shrink-0 items-center justify-between gap-2 border-b border-slate-100 bg-gradient-to-r from-[#2D7FF9]/10 to-slate-50/90 px-3",
          headerPy,
        )}
      >
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#1a5fc4]">Qualification</span>
        <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-[#2D7FF9]">
          Ouvrir l&apos;annonce
          <ArrowUpRight className="h-3 w-3" aria-hidden />
        </span>
      </div>
      <div
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
        aria-hidden
      >
        <QualificationPanelBody
          dossier={dossier}
          produit={produit}
          loading={loading}
          error={error}
          maxRows={maxRows}
        />
      </div>
    </Link>
  );
}

/**
 * Grille marketplace : retournement 3D latéral au survol (qualifications au verso).
 */
export default function DossierHoverPreview({
  dossier: seedDossier,
  dossierId,
  produits = [],
  href,
  onBeforeOpen,
  children,
  disabled = false,
}) {
  const canHover = useFinePointerHover();
  const reducedMotion = usePrefersReducedMotion();
  const id = String(dossierId || seedDossier?.dossier_id || seedDossier?.id || "").trim();
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(() => (id ? previewCache.get(id) : null) || seedDossier || null);
  const [produitExtra, setProduitExtra] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const dossier = detail || seedDossier;
  const linkHref = href || `/dossier/${id}`;
  const cardTitle = dossier?.title || seedDossier?.title || "";

  const produitFromList = useMemo(
    () => produits.find((p) => String(p.produit_id) === String(dossier?.produit_id || "")),
    [produits, dossier?.produit_id],
  );

  const produit = useMemo(() => {
    if (produitFromList?.champs?.length) return produitFromList;
    if (produitExtra?.champs?.length) return produitExtra;
    return produitFromList || produitExtra;
  }, [produitFromList, produitExtra]);

  const clearTimers = useCallback(() => {
    if (openTimerRef.current) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const loadDetail = useCallback(
    async (force = false) => {
      if (!id) return;
      const cached = previewCache.get(id);
      if (!force && cached && customFieldsHasValues(cached)) {
        setDetail(cached);
        setLoading(false);
        setError("");
        return;
      }
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get(`/dossiers/${id}`);
        previewCache.set(id, data);
        setDetail(data);
      } catch (e) {
        setError(e?.response?.data?.detail || e?.message || "Impossible de charger l’aperçu");
        setDetail(seedDossier || cached || null);
      } finally {
        setLoading(false);
      }
    },
    [id, seedDossier],
  );

  const loadProduit = useCallback(
    async (produitId) => {
      const pid = String(produitId || "").trim();
      if (!pid) return;
      if (produitCache.has(pid)) {
        setProduitExtra(produitCache.get(pid));
        return;
      }
      const fromList = produits.find((p) => String(p.produit_id) === pid);
      if (fromList?.champs?.length) {
        produitCache.set(pid, fromList);
        setProduitExtra(fromList);
        return;
      }
      try {
        const { data } = await api.get(`/produits/${pid}`);
        produitCache.set(pid, data);
        setProduitExtra(data);
      } catch {
        /* repli custom_fields */
      }
    },
    [produits],
  );

  const preload = useCallback(() => {
    if (!id) return;
    if (seedDossier && !detail) setDetail(seedDossier);
    void loadDetail(false);
    const pid = seedDossier?.produit_id || detail?.produit_id;
    if (pid) void loadProduit(pid);
  }, [id, seedDossier, detail, loadDetail, loadProduit]);

  const scheduleOpen = useCallback(() => {
    clearTimers();
    preload();
    openTimerRef.current = window.setTimeout(() => setOpen(true), OPEN_DELAY_MS);
  }, [clearTimers, preload]);

  const scheduleClose = useCallback(() => {
    clearTimers();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, [clearTimers]);

  const cancelClose = useCallback(() => {
    clearTimers();
  }, [clearTimers]);

  useEffect(() => {
    if (!open || !id) return;
    void loadDetail(true);
  }, [open, id, loadDetail]);

  useEffect(() => {
    if (!id) return;
    const pid = dossier?.produit_id;
    if (!pid || produit?.champs?.length) return;
    void loadProduit(pid);
  }, [id, dossier?.produit_id, produit?.champs?.length, loadProduit]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  if (disabled || !canHover || !id) {
    return children;
  }

  const { minH } = FLIP_SHELL;

  const qualProps = {
    dossier,
    produit,
    loading,
    error,
    linkHref,
    title: cardTitle,
    onBeforeOpen,
    onPointerEnter: cancelClose,
    onPointerLeave: scheduleClose,
    shell: FLIP_SHELL,
  };

  const flipped = open && !reducedMotion;
  const showBack = open && reducedMotion;

  return (
    <div
      className={cn("relative h-full w-full min-w-0 [perspective:1100px]", minH, open && "z-[45]")}
      data-testid={`dossier-hover-flip-${id}`}
      onPointerEnter={scheduleOpen}
      onPointerLeave={scheduleClose}
      onFocus={scheduleOpen}
      onBlur={scheduleClose}
    >
      <div
        className={cn(
          "relative h-full w-full transition-transform duration-500 ease-out [transform-style:preserve-3d]",
          minH,
          flipped && "[transform:rotateY(180deg)]",
        )}
      >
        <div
          className={cn(
            "relative h-full w-full [backface-visibility:hidden]",
            (flipped || showBack) && "pointer-events-none",
            showBack && "invisible",
          )}
          aria-hidden={showBack}
        >
          {children}
        </div>

        {reducedMotion ? (
          showBack ? (
            <div className={cn("absolute inset-0 h-full", minH)}>
              <QualificationBackFace {...qualProps} flipFace={false} shell={FLIP_SHELL} />
            </div>
          ) : null
        ) : (
          <QualificationBackFace {...qualProps} flipFace />
        )}
      </div>
    </div>
  );
}
